/**
 * Equities — live context desk for a small owner-curated watchlist.
 *
 * Deliberately isolated from SPX: no decision pills, no epochs, no rules —
 * just observation. Pin snapshots are append-only. /equities/* is its own
 * workspace in the shell (AppShell workspace="equities": own rail, accent and
 * status bar), so it never reads as part of the SPX decision desk.
 * Symbols live under /equities/s/:symbol; old /equities/:symbol links redirect.
 *
 * Scanner states (ACTIONABLE NOW, IN ZONE …) are research labels, not decisions:
 * they never borrow decision colour. Price moves use Delta (blue / orange text).
 */
import React, { useState, useEffect, useRef, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link, Outlet, useNavigate, useParams } from "react-router-dom";
import { createChart } from "lightweight-charts";
import { api, fmtErr, client } from "@/lib/api";
import { toast } from "sonner";
import { Send } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import {
  Badge, Button, Checkbox, DATA_STATES, DataStateBadge, Delta, EmptyState, ErrorState, Icon, IconButton,
  Modal, Num, PageHead, PageLoading, Panel, RuleResult, SegmentedControl, Skeleton, StatCell, TextField,
  DataBanner, dateTimeET, etSyd, fmt, lwcOptions, usePageTitle, usePrefs, etAndLocal } from "@/sentinel";

// ── shared helpers ───────────────────────────────────────────────────────────

export const isNum = (n) => n !== null && n !== undefined && n !== "" && !isNaN(n);
/** Equity prices: 2dp with thousands separators and a true minus. */
export const px = (n) => (isNum(n) ? fmt(Number(n), "price") : "—");

function priceChange(last, prior) {
  if (!last || !prior) return null;
  const net = last - prior;
  return { net, pct: (net / prior) * 100 };
}
function fmtAge(quote_ts) {
  if (!quote_ts) return "—";
  const then = typeof quote_ts === "number" ? quote_ts : new Date(quote_ts).getTime();
  const age = Math.round((Date.now() - then) / 1000);
  if (age < 60) return `${age}s`;
  if (age < 3600) return `${Math.round(age / 60)}m`;
  return `${Math.round(age / 3600)}h`;
}
/** True from 09:25 to 16:05 ET on weekdays. Holidays and early closes still poll, at the slow rate after the close. */
function usMarketHours(now = new Date()) {
  const p = new Intl.DateTimeFormat("en-GB", { timeZone: "America/New_York", weekday: "short", hour: "2-digit", minute: "2-digit", hourCycle: "h23" }).formatToParts(now);
  const g = (t) => p.find((x) => x.type === t)?.value;
  if (g("weekday") === "Sat" || g("weekday") === "Sun") return false;
  const m = Number(g("hour")) * 60 + Number(g("minute"));
  return m >= 565 && m < 965;
}
/** A react-query refetchInterval: `ms` while the US market is open, every 10 minutes otherwise. */
export const eqPoll = (ms) => () => (usMarketHours() ? ms : 10 * 60 * 1000);
/** Symbol detail path. */
export const symbolPath = (sym) => `/equities/s/${encodeURIComponent(sym)}`;

/** Price-direction colour for a signed value, as text only. */
const dirClass = (v) => (v > 0 ? "sn-price-up" : v < 0 ? "sn-price-down" : "sn-price-flat");

/** Feed recency → how a number renders its own provenance. */
const REC_NUM_STATE = { LIVE: "live", DEGRADED: "delayed", STALE: "stale", STUB: "stub" };

/** Stocks feed recency as a provenance badge (LIVE / DEGRADED / STALE / STUB / UNAVAILABLE). */
function RecencyBadge({ recency, detail = "stocks", ...rest }) {
  const r = String(recency || "UNAVAILABLE").toUpperCase();
  if (r === "DEGRADED") {
    return (
      <Badge className="sn-prov" icon="Clock" title="Some quotes are delayed or partial" {...rest}>
        DEGRADED{detail && <span style={{ fontWeight: 400, letterSpacing: 0 }}>· {detail}</span>}
      </Badge>
    );
  }
  return <DataStateBadge state={DATA_STATES[r] ? r : "UNAVAILABLE"} detail={detail || undefined} {...rest} />;
}

// ── layout shell ─────────────────────────────────────────────────────────────

/** Section navigation lives in the equities workspace rail (and tab bar on mobile); this only frames the page. */
export function EquitiesLayout() {
  return (
    <div className="sn-stack" style={{ gap: "var(--space-5)" }} data-testid="equities-shell">
      <PageHead
        crumb={["Side experiments"]}
        title="Side experiments"
        context="Paper records of ideas outside the main 10:00 am SPX programme: fund discounts, SPAC trust values, tender offers and the trend benchmark. Kept running at a modest pace, each judged on its own date. Not financial advice; Sentinel places no orders."
      />
      <Outlet />
    </div>
  );
}

// ── Opportunities page (swing-setup scanner) ─────────────────────────────────

export const SETUP_LABEL = {
  trend_pullback: "Trend pullback", volume_breakout: "Volume breakout", earnings_drift: "Earnings drift",
};

function fmtCap(v) {
  if (v == null) return "—";
  const a = Math.abs(v);
  if (a >= 1e12) return `$${(v / 1e12).toFixed(2)}T`;
  if (a >= 1e9) return `$${(v / 1e9).toFixed(1)}B`;
  if (a >= 1e6) return `$${(v / 1e6).toFixed(0)}M`;
  return fmt(v, "usd");
}
const fmtPct = (v) => (v == null ? "—" : fmt(v * 100, "pct"));
const fmtSignedPct = (v) => (v == null ? "—" : fmt(v * 100, "pct", { signed: true }));
const fmtR = (v) => (isNum(v) ? `${Number(v) >= 0 ? "+" : "−"}${Math.abs(Number(v)).toFixed(2)}R` : "—");

/**
 * Scanner states are NOT decisions. ACTIONABLE NOW is a solid neutral badge (static, no pulse);
 * INVALIDATED reads as a validation fail; NO QUOTE as unavailable data; NEEDS REVIEW as unconfirmed.
 * "IN ZONE · …" states name the one condition still missing before ACTIONABLE NOW.
 */
export function ScannerState({ state, ...rest }) {
  if (state?.startsWith("IN ZONE")) return <Badge icon="Target" {...rest}>{state}</Badge>;
  switch (state) {
    case "ACTIONABLE NOW": return <Badge tone="solid" icon="CircleDot" {...rest}>{state}</Badge>;
    case "WAITING FOR ENTRY": return <Badge icon="Clock" {...rest}>{state}</Badge>;
    case "EXTENDED": return <Badge icon="ArrowUpRight" {...rest}>{state}</Badge>;
    case "OPEN": return <Badge icon="Activity" {...rest}>OPEN (MODEL)</Badge>;
    case "INVALIDATED": return <RuleResult result="FAIL" {...rest}>{state}</RuleResult>;
    case "NO QUOTE": return <Badge className="sn-prov" icon="CircleSlash" {...rest}>{state}</Badge>;
    case "NEEDS REVIEW": return <RuleResult result="UNCONFIRMED" {...rest}>{state}</RuleResult>;
    case "REGIME HOLD": return <Badge icon="Snowflake" {...rest}>{state}</Badge>;
    default: return <Badge {...rest}>{state}</Badge>;
  }
}

function LiveBadge({ live, fallback }) {
  const state = live?.state || fallback || "—";
  const age = live?.quote_age_s;
  const freshTxt = live
    ? (live.session === "closed" ? "market closed"
       : live.quote_ok ? `quote ok${age != null ? ` · ${age}s` : ""}` : (live.quote_issue || "no quote"))
    : null;
  return (
    <span className="sn-stack" style={{ gap: 2, alignItems: "flex-end" }}>
      <ScannerState state={state} data-testid="opp-live-state" />
      {freshTxt && <span className="sn-small sn-faint">{freshTxt}</span>}
    </span>
  );
}

const REGIME_LABEL = { RISK_ON: "Risk-on", NEUTRAL: "Neutral", RISK_OFF: "Risk-off" };
const REGIME_EFFECT = {
  RISK_ON: "All qualifying setups can be acted on.",
  NEUTRAL: "Only the top 5 setups are actionable; the rest are on hold.",
  RISK_OFF: "Long setups are on hold. They are still tracked so the rule can be tested.",
};

export function RegimeStrip({ regime, ...rest }) {
  if (!regime) return null;
  return (
    <div className="sn-row" style={{ gap: "var(--space-3)", alignItems: "flex-start", flexWrap: "wrap" }} {...rest}>
      <Badge tone={regime.state === "RISK_ON" ? "solid" : "neutral"}
             icon={regime.state === "RISK_OFF" ? "TrendingDown" : regime.state === "RISK_ON" ? "TrendingUp" : "Minus"}
             data-testid="regime-state">
        Market: {REGIME_LABEL[regime.state] || regime.state}
      </Badge>
      <span className="sn-small sn-muted" style={{ flex: "1 1 280px" }}>
        {REGIME_EFFECT[regime.state]}{" "}
        <span className="sn-faint">{(regime.notes || []).join(" · ")}</span>
      </span>
    </div>
  );
}

function CompanyContext({ ctx, p }) {
  const fu = p.fundamentals || {};
  const heads = (ctx?.headlines || []).filter((h) => h.title);
  const hasFu = ["rev_yoy", "eps_yoy", "beats_4q", "eps_revision_30d"].some((k) => fu[k] != null);
  if (!ctx && !p.next_earnings && !hasFu) return null;
  const symbol = p.symbol;
  return (
    <div className="sn-stack" style={{ gap: "var(--space-3)", paddingTop: "var(--space-3)", borderTop: "1px solid var(--border-hairline)" }}
         data-testid={`opp-context-${symbol}`}>
      {(ctx?.name || ctx?.sector) && (
        <div className="sn-small" style={{ color: "var(--text-primary)", fontWeight: 500 }}>
          {ctx?.name || symbol}
          {ctx?.sector && <span className="sn-faint" style={{ fontWeight: 400 }}> · {ctx.sector}{ctx?.industry ? ` / ${ctx.industry}` : ""}</span>}
        </div>
      )}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatCell size="sm" label="Mkt cap" value={fmtCap(ctx?.market_cap)} data-testid={`opp-mktcap-${symbol}`} />
        <StatCell size="sm" label="P/E" value={ctx?.pe != null ? fmt(Number(ctx.pe), "pts") : "—"} data-testid={`opp-pe-${symbol}`} />
        <StatCell size="sm" label="Margin" value={fmtPct(ctx?.profit_margin)} data-testid={`opp-margin-${symbol}`} />
        <StatCell size="sm" label="Next ER" value={p.next_earnings || "—"} data-testid={`opp-nexter-${symbol}`} />
      </div>
      {hasFu && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid={`opp-fundamentals-${symbol}`}>
          <StatCell size="sm" label="Revenue YoY" value={fmtSignedPct(fu.rev_yoy)}
                    sub={fu.rev_accel == null ? null : fu.rev_accel ? "accelerating" : "slowing"} />
          <StatCell size="sm" label="EPS YoY" value={fmtSignedPct(fu.eps_yoy)}
                    sub={fu.eps_accel == null ? null : fu.eps_accel ? "accelerating" : "slowing"} />
          <StatCell size="sm" label="Beats (4Q)" value={fu.beats_4q != null ? `${fu.beats_4q}/4` : "—"} />
          <StatCell size="sm" label="EPS est. 30d" value={fmtSignedPct(fu.eps_revision_30d)}
                    sub={fu.eps_revision_30d == null ? "building history" : null} />
        </div>
      )}
      {heads.length > 0 && (
        <div className="sn-stack" style={{ gap: "var(--space-1)" }} data-testid={`opp-news-${symbol}`}>
          <span className="sn-micro">Recent headlines</span>
          <ul className="sn-stack" style={{ gap: "var(--space-1)", listStyle: "none", margin: 0, padding: 0 }}>
            {heads.map((h, i) => (
              <li key={i} className="sn-small">
                <a href={h.url} target="_blank" rel="noreferrer" className="sn-link" style={{ fontSize: 12, display: "inline" }}>
                  {h.title}
                </a>
                <span className="sn-faint"> — {h.site || "news"}{h.date ? ` · ${h.date}` : ""}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

const EVENT_LABEL = {
  ISSUED: "Issued", TRIGGERED: "Entry filled (model)", TARGET: "Target hit", STOPPED: "Stopped out",
  TIME_EXIT: "Time exit (session 10)", EXPIRED: "Expired untriggered", INVALIDATED: "Invalidated",
  SUPERSEDED: "Superseded by a newer plan", MISSED_GAP: "Gapped above the zone",
};

export function PlanLifecycleModal({ planId, onClose }) {
  const { data, isLoading } = useQuery({ queryKey: ["eq-plan", planId], queryFn: () => api.eqPlan(planId), enabled: !!planId });
  const o = data?.outcome;
  return (
    <Modal title={data ? `${data.plan.symbol} · ${SETUP_LABEL[data.plan.setup] || data.plan.setup}` : "Plan"}
           icon="History" onClose={onClose} width={560} data-testid="plan-lifecycle-modal">
      {isLoading || !data ? <Skeleton lines={4} /> : (
        <div className="sn-stack">
          <div className="grid grid-cols-3 gap-3">
            <StatCell size="sm" label="Status (model)" value={o?.status || "—"} />
            <StatCell size="sm" label="Entry" value={o?.entry_price ? `${px(o.entry_price)} · ${o.entry_date}` : "—"} />
            <StatCell size="sm" label={o?.r_multiple != null ? "Result" : "Unrealised"} value={fmtR(o?.r_multiple ?? o?.unrealized_r)} />
          </div>
          <ol className="sn-stack" style={{ gap: "var(--space-2)", margin: 0, paddingLeft: "var(--space-4)" }} data-testid="plan-events">
            {(data.events || []).map((e, i) => (
              <li key={i} className="sn-small">
                <span className="sn-num">{e.date}</span> — {EVENT_LABEL[e.type] || e.type}
                {e.price != null && <span className="sn-num"> @ {px(e.price)}</span>}
                {e.r != null && <span className="sn-num"> ({fmtR(e.r)})</span>}
                {e.ambiguous && <span className="sn-faint"> · stop and target in the same bar; stop assumed</span>}
                {e.detail && <span className="sn-faint"> · {e.detail}</span>}
              </li>
            ))}
          </ol>
          <p className="sn-small sn-faint" style={{ margin: 0 }}>
            Model outcome from daily bars under one fixed fill/exit policy — not your fills. Plan levels never change after issue.
          </p>
        </div>
      )}
    </Modal>
  );
}

function Flag({ icon, children, testId }) {
  return (
    <p className="sn-row sn-small" style={{ margin: 0, gap: "var(--space-1)", color: "var(--text-primary)", flexWrap: "nowrap", alignItems: "flex-start" }}
       data-testid={testId}>
      <Icon name={icon} size={14} style={{ color: "var(--text-tertiary)", flex: "none", marginTop: 2 }} />
      <span>{children}</span>
    </p>
  );
}

function OppCard({ p, sizer, onHistory }) {
  const risk = p.risk_per_share;
  const acct = Number(sizer?.account) || 0;
  const rp = Number(sizer?.riskPct) || 0;
  const shares = acct > 0 && rp > 0 && risk > 0 ? Math.floor((acct * rp / 100) / risk) : 0;
  const dollarRisk = shares * risk;
  const cost = shares * (p.entry_zone_high || 0);
  const potential = shares * (p.reward_per_share || 0);
  const fx = p.factors || {};
  const open = p.outcome?.status === "TRIGGERED";
  const liveLast = p.live?.last != null;
  const e = p.earnings || {};
  return (
    <Panel
      data-testid={`opp-card-${p.symbol}`}
      title={
        <span className="sn-row" style={{ gap: "var(--space-2)", flexWrap: "nowrap" }}>
          {p.rank != null && <span className="sn-rank" data-testid={`opp-rank-${p.symbol}`}>#{p.rank}</span>}
          <span data-testid={`opp-symbol-${p.symbol}`}>{p.symbol}</span>
        </span>
      }
      meta={<LiveBadge live={p.live} fallback={open ? "OPEN" : p.actionability} />}
    >
      <div className="sn-stack">
        <div className="sn-row" style={{ justifyContent: "space-between" }}>
          <span className="sn-small sn-muted">
            {SETUP_LABEL[p.setup] || p.setup}
            {p.setup_rank != null && <span className="sn-faint"> · #{p.setup_rank} of its setup</span>}
            <span className="sn-faint"> · signal {p.signal_date}</span>
          </span>
          {p.score != null && (
            <span className="sn-small" title="Composite score (momentum, extension, tightness, volume, sector, reach)"
                  data-testid={`opp-score-${p.symbol}`}>
              Score <span className="sn-num" style={{ color: "var(--text-primary)" }}>{Number(p.score).toFixed(0)}</span>
            </span>
          )}
        </div>

        {open && (
          <p className="sn-small" style={{ margin: 0, padding: "var(--space-2)", background: "var(--surface-sunken)", borderRadius: "var(--radius-sm)" }}
             data-testid={`opp-open-${p.symbol}`}>
            Model entry <span className="sn-num">{px(p.outcome.entry_price)}</span> on {p.outcome.entry_date}
            {" · "}day <span className="sn-num">{p.outcome.days_held ?? "—"}</span> of 10
            {" · "}<span className="sn-num">{fmtR(p.live?.unrealized_r ?? p.outcome.unrealized_r)}</span>
          </p>
        )}

        <div className="grid grid-cols-3 gap-3">
          <StatCell size="sm" label="Entry zone" value={`${px(p.entry_zone_low)}–${px(p.entry_zone_high)}`} data-testid={`opp-entry-${p.symbol}`} />
          <StatCell size="sm" label="Stop" value={px(p.stop)} data-testid={`opp-stop-${p.symbol}`} />
          <StatCell size="sm" label="Target" value={px(p.target)} data-testid={`opp-target-${p.symbol}`} />
          <StatCell size="sm" label="Net R:R" value={`${isNum(p.net_rr) ? Number(p.net_rr).toFixed(1) : "—"}×`} data-testid={`opp-rr-${p.symbol}`} />
          <StatCell size="sm" label="Risk / share" value={isNum(p.risk_per_share) ? `$${px(p.risk_per_share)}` : "—"} data-testid={`opp-risk-${p.symbol}`} />
          <StatCell size="sm" label={liveLast ? "Last" : "Momentum"}
                    value={liveLast ? px(p.live.last) : fx.rs_pct != null ? `P${Number(fx.rs_pct).toFixed(0)}` : "—"}
                    data-testid={`opp-last-${p.symbol}`} />
        </div>

        {shares > 0 && (
          <p className="sn-small sn-muted" style={{ margin: 0, padding: "var(--space-2)", background: "var(--surface-sunken)", borderRadius: "var(--radius-sm)" }}
             data-testid={`opp-sizing-${p.symbol}`}>
            <span className="sn-num" style={{ color: "var(--text-primary)" }}>{shares.toLocaleString("en-US")} sh</span>
            {" · "}risk <span className="sn-num">{fmt(dollarRisk, "usd")}</span>
            {" · "}cost ≈ <span className="sn-num">{fmt(cost, "usd")}</span>
            {" · "}potential <span className="sn-num">{fmt(potential, "usd", { signed: true })}</span>
            {p.crowding_flag && <span> · counts toward one sector position</span>}
          </p>
        )}

        <div className="sn-stack" style={{ gap: "var(--space-1)" }}>
          <span className="sn-micro">Why this setup</span>
          <ul className="sn-small sn-muted" style={{ margin: 0, paddingLeft: "var(--space-4)" }} data-testid={`opp-reasons-${p.symbol}`}>
            {(p.reasons || []).map((r, i) => <li key={i}>{r}</li>)}
          </ul>
        </div>

        {p.earnings_flag && <Flag icon="CalendarDays" testId={`opp-earnings-${p.symbol}`}>{p.earnings_flag}</Flag>}
        {!p.earnings_flag && e.status === "CLEAR" && (
          <Flag icon="CircleCheck" testId={`opp-earnings-${p.symbol}`}>Earnings checked: {e.note}</Flag>
        )}
        {p.regime_flag && <Flag icon="Snowflake" testId={`opp-regime-${p.symbol}`}>{p.regime_flag}</Flag>}
        {p.crowding_flag && <Flag icon="Layers" testId={`opp-crowding-${p.symbol}`}>{p.crowding_flag}</Flag>}
        {p.reach_flag && <Flag icon="Gauge" testId={`opp-reach-${p.symbol}`}>{p.reach_flag}</Flag>}

        <CompanyContext ctx={p.context} p={p} />

        <div className="sn-row" style={{ justifyContent: "space-between", alignItems: "flex-end" }}>
          <p className="sn-small sn-faint sn-sans-num" style={{ margin: 0, flex: 1 }}>
            Close {px(p.close)} · ATR14 {px(p.atr14)}
            {fx.ext_atr != null && ` · ${Number(fx.ext_atr).toFixed(1)} ATR above EMA20`}
            {fx.range_contraction != null && ` · range ${Number(fx.range_contraction).toFixed(2)}× normal`}
            {fx.target_atr != null && ` · target ${Number(fx.target_atr).toFixed(1)} ATR away`}
            {p.implied?.implied_move != null && ` · implied 10-day move ±${px(p.implied.implied_move)}`}
            {" · "}{p.expiry_note}
          </p>
          {p.id && (
            <Button size="sm" variant="ghost" icon="History" onClick={() => onHistory(p.id)} data-testid={`opp-history-${p.symbol}`}>
              History
            </Button>
          )}
        </div>
      </div>
    </Panel>
  );
}

const SCAN_PHASE = {
  starting: "Starting…", syncing_bars: "Updating the daily-bar cache", loading: "Loading history",
  earnings_calendar: "Checking the earnings calendar", scoring: "Scoring the universe",
  verifying: "Verifying earnings dates for the shortlist", context: "Fetching company context and options",
  issuing: "Issuing plans", tracking: "Updating the track record",
};

function FormingToday({ rows, at }) {
  if (!rows?.length) return null;
  return (
    <Panel flush title="Forming today" icon="Hourglass"
           meta={<span className="sn-small sn-faint">{at ? `as of ${etSyd(at)}` : ""}</span>} data-testid="forming-today">
      <p className="sn-small sn-muted" style={{ margin: 0, padding: "var(--space-3) var(--space-4)" }}>
        Would qualify if today closed at the current price (volume projected to the full session). Not issued or tracked until the close confirms it.
      </p>
      <div className="sn-table-wrap" style={{ border: 0, borderRadius: 0 }}>
        <table className="sn-table" aria-label="Setups forming today">
          <thead><tr>
            <th>Symbol</th><th>Setup</th><th className="r">Score</th><th className="r">Entry zone</th>
            <th className="r">Stop</th><th className="r">Target</th>
          </tr></thead>
          <tbody>
            {rows.map((f) => (
              <tr key={f.symbol} data-testid={`forming-${f.symbol}`}>
                <td className="id">{f.symbol}</td>
                <td>{SETUP_LABEL[f.setup] || f.setup}</td>
                <td className="num">{f.score != null ? Number(f.score).toFixed(0) : "—"}</td>
                <td className="num">{px(f.entry_zone_low)}–{px(f.entry_zone_high)}</td>
                <td className="num">{px(f.stop)}</td>
                <td className="num">{px(f.target)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Panel>
  );
}

const FILTERS = [
  { value: "all", label: "All" },
  { value: "volume_breakout", label: "Breakout" },
  { value: "trend_pullback", label: "Pullback" },
  { value: "earnings_drift", label: "Earnings drift" },
  { value: "open", label: "Open (model)" },
];

export function EquitiesOpportunities() {
  const { can } = useAuth();
  const qc = useQueryClient();

  const { data: scans } = useQuery({
    queryKey: ["eq-scans"], queryFn: api.eqScans,
    refetchInterval: (q) => (q.state.data?.running ? 4000 : false),
  });
  const running = scans?.running;

  const { data } = useQuery({
    queryKey: ["eq-opportunities"], queryFn: api.eqOpportunities,
    refetchInterval: eqPoll(30000),
  });

  const startScan = useMutation({
    mutationFn: () => api.eqStartScan(),
    onSuccess: () => {
      toast.success("Scan started — this takes a few minutes the first time, about a minute after that.");
      qc.invalidateQueries({ queryKey: ["eq-scans"] });
    },
    onError: (e) => toast.error(fmtErr(e.response?.data?.detail) || e.message),
  });

  const allPlans = useMemo(() => data?.plans || [], [data]);
  const summary = data?.summary;
  const prog = running?.progress;
  const [filter, setFilter] = useState("all");
  const [historyId, setHistoryId] = useState(null);
  const plans = allPlans.filter((p) => filter === "all" ? true
    : filter === "open" ? p.outcome?.status === "TRIGGERED" : p.setup === filter);
  const counts = useMemo(() => {
    const c = { all: allPlans.length, open: 0 };
    for (const p of allPlans) {
      c[p.setup] = (c[p.setup] || 0) + 1;
      if (p.outcome?.status === "TRIGGERED") c.open += 1;
    }
    return c;
  }, [allPlans]);

  const [sizer, setSizer] = useState(() => {
    try { return JSON.parse(localStorage.getItem("eq_sizer")) || { account: "", riskPct: "1" }; }
    catch { return { account: "", riskPct: "1" }; }
  });
  const updSizer = (k, v) => {
    const next = { ...sizer, [k]: v };
    setSizer(next);
    try { localStorage.setItem("eq_sizer", JSON.stringify(next)); } catch { /* private mode */ }
  };
  const [showAlerts, setShowAlerts] = useState(false);

  return (
    <div className="sn-stack" style={{ gap: "var(--space-5)" }} data-testid="equities-opportunities">
      {/* controls + summary */}
      <Panel
        title="Swing scanner"
        icon="ScanSearch"
        actions={
          <>
            {can("owner") && (
              <Button size="sm" variant="ghost" icon="Bell" aria-pressed={showAlerts}
                      onClick={() => setShowAlerts((s) => !s)} data-testid="alerts-toggle-btn">
                Alerts
              </Button>
            )}
            {can("analyst") && (
              <Button size="sm" variant="primary" icon="RefreshCw" loading={!!running}
                      onClick={() => startScan.mutate()} disabled={!!running || startScan.isPending}
                      data-testid="run-scan-btn">
                {running ? "Scanning…" : "Run scan"}
              </Button>
            )}
          </>
        }
      >
        <div className="sn-stack">
          <RegimeStrip regime={data?.regime} data-testid="regime-strip" />
          <div className="sn-stack" style={{ gap: "var(--space-1)" }}>
            <p className="sn-sans-num" style={{ margin: 0, color: "var(--text-secondary)" }} data-testid="scan-summary">
              {summary
                ? `Universe ${summary.universe_count} liquid stocks · ${summary.qualified} qualified`
                  + ` (${summary.actionable} actionable, ${summary.needs_review} need review, ${summary.regime_hold ?? 0} on hold)`
                  + ` · as of ${summary.as_of}${summary.provisional ? ` (after-close bar, re-checked at ${etAndLocal("08:00")})` : ""}`
                : "No completed scan yet."}
            </p>
            {data?.live_updated_at && (
              <p className="sn-small sn-faint" style={{ margin: 0 }} data-testid="live-updated">
                Live quotes {data.market_session === "closed" ? "(market closed)" : `(${data.market_session})`} · updated{" "}
                <span className="sn-num">{etSyd(data.live_updated_at)}</span>
                {" · "}ACTIONABLE NOW needs a completed 5-minute close above the trigger, a fresh quote with the ask in the zone, {etAndLocal("09:45")} to 30 min before the close.
              </p>
            )}
            {(summary?.crowding || []).map((c) => (
              <p key={c.sector} className="sn-small" style={{ margin: 0 }} data-testid="crowding-summary">
                <Icon name="Layers" size={14} /> {c.count} of the top 5 are {c.sector} ({c.symbols.join(", ")}) — they tend to move together.
              </p>
            ))}
          </div>

          {running && (
            <p className="sn-row sn-small sn-muted" style={{ margin: 0, gap: "var(--space-1)" }} data-testid="scan-progress">
              <Icon name="Hourglass" size={14} />
              {SCAN_PHASE[prog?.phase] || `Phase: ${prog?.phase || "starting"}`}
              {prog?.total ? ` · ${prog.done || 0}/${prog.total}` : ""}
            </p>
          )}
          {data?.research_status && (
            <div className="sn-row" style={{ gap: "var(--space-2)" }}>
              <Badge icon="Info" data-testid="research-status">{data.research_status}</Badge>
              <Link to="/equities/track-record" className="sn-link sn-small">See the track record</Link>
            </div>
          )}

          <hr className="sn-hr" />

          {/* Position sizer */}
          <div className="sn-row" style={{ alignItems: "flex-end", gap: "var(--space-3)" }} data-testid="position-sizer">
            <span className="sn-micro" style={{ alignSelf: "center" }}>Position sizer</span>
            <div style={{ width: 144 }}>
              <TextField label="Account ($)" type="number" min="0" numeric value={sizer.account}
                         onChange={(e) => updSizer("account", e.target.value)} placeholder="25000" data-testid="sizer-account" />
            </div>
            <div style={{ width: 112 }}>
              <TextField label="Risk (%)" type="number" min="0" max="100" step="0.25" numeric value={sizer.riskPct}
                         onChange={(e) => updSizer("riskPct", e.target.value)} data-testid="sizer-risk-pct" />
            </div>
            <span className="sn-help" style={{ paddingBottom: "var(--space-2)" }}>
              Sizes each pick to risk <span className="sn-num">{sizer.riskPct || 0}%</span> of your account to its stop. A gap can lose more than that.
            </span>
          </div>

          {showAlerts && <AlertsSettings />}
        </div>
      </Panel>

      {allPlans.length > 0 && (
        <SegmentedControl label="Filter setups" value={filter} onChange={setFilter} data-testid="opp-filter"
                          options={FILTERS.map((f) => ({ ...f, label: `${f.label} (${counts[f.value] || 0})`, testId: `opp-filter-${f.value}` }))} />
      )}

      {/* plans */}
      {plans.length === 0 ? (
        <EmptyState
          icon="ScanSearch"
          data-testid="opp-empty"
          title={running ? "Scanning the market for swing setups…" : allPlans.length ? "Nothing in this filter" : "No qualifying setups"}
          action={!running && can("analyst") && !data?.scan_id ? (
            <Button icon="RefreshCw" onClick={() => startScan.mutate()} data-testid="run-scan-btn-empty">Run first scan</Button>
          ) : null}
        >
          {running ? "Results appear here when the scan completes."
            : allPlans.length ? "No plans of this type are in play. Pick another filter."
            : data?.note || "No qualifying setups in the latest scan. The rules are strict — no setup is a valid result."}
        </EmptyState>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="opp-grid">
          {plans.map((p) => <OppCard key={p.id || p.symbol} p={p} sizer={sizer} onHistory={setHistoryId} />)}
        </div>
      )}

      <FormingToday rows={data?.forming} at={data?.forming_generated_at} />

      {(data?.suppressed || []).length > 0 && (
        <p className="sn-small sn-faint" style={{ margin: 0 }} data-testid="opp-suppressed">
          Not re-issued: {data.suppressed.map((s) => `${s.symbol} (${s.suppressed})`).join("; ")}.
        </p>
      )}

      {data?.disclaimer && (
        <p className="sn-small sn-faint" style={{ margin: 0 }} data-testid="opp-disclaimer">
          {data.disclaimer}
        </p>
      )}
      {historyId && <PlanLifecycleModal planId={historyId} onClose={() => setHistoryId(null)} />}
    </div>
  );
}

function AlertsSettings() {
  const qc = useQueryClient();
  const { data: cfg } = useQuery({ queryKey: ["eq-alert-config"], queryFn: api.eqAlertConfig });
  const [enabled, setEnabled] = useState(false);
  const [emails, setEmails] = useState("");
  useEffect(() => {
    if (cfg) { setEnabled(!!cfg.enabled); setEmails((cfg.recipients || []).join(", ")); }
  }, [cfg]);

  const save = useMutation({
    mutationFn: () => api.eqSetAlertConfig({
      enabled,
      recipients: emails.split(/[,\n]/).map((e) => e.trim()).filter(Boolean),
    }),
    onSuccess: () => { toast.success("Alert settings saved"); qc.invalidateQueries({ queryKey: ["eq-alert-config"] }); },
    onError: (e) => toast.error(fmtErr(e.response?.data?.detail) || e.message),
  });
  const test = useMutation({
    mutationFn: api.eqTestAlert,
    onSuccess: (d) => toast.success(`Test email sent to ${d.recipients.length} recipient(s)`),
    onError: (e) => toast.error(fmtErr(e.response?.data?.detail) || e.message),
  });

  return (
    <div className="sn-stack" style={{ paddingTop: "var(--space-3)", borderTop: "1px solid var(--border-hairline)" }} data-testid="alerts-settings">
      <span className="sn-micro">Actionable alerts (email)</span>
      <Checkbox label="Email me when a pick flips to ACTIONABLE NOW" checked={enabled} onChange={setEnabled}
                data-testid="alerts-enabled-toggle" />
      <div className="sn-field">
        <label className="sn-field-label" htmlFor="eq-alert-recipients">Recipients</label>
        <div className="sn-input-wrap" style={{ height: "auto" }}>
          <textarea id="eq-alert-recipients" value={emails} onChange={(e) => setEmails(e.target.value)}
                    placeholder="you@example.com, desk@example.com" rows={2}
                    data-testid="alerts-recipients-input"
                    className="sn-input sn-num" style={{ padding: "var(--space-2)", resize: "vertical", fontSize: 12 }} />
        </div>
        <span className="sn-help">Separate addresses with commas or new lines.</span>
      </div>
      <div className="sn-row">
        <Button size="sm" variant="primary" onClick={() => save.mutate()} loading={save.isPending} data-testid="alerts-save-btn">
          Save
        </Button>
        <Button size="sm" icon={Send} onClick={() => test.mutate()} disabled={test.isPending} data-testid="alerts-test-btn">
          Send test
        </Button>
        <span className="sn-help">
          {cfg?.email_available ? "Email ready" : "Email unavailable"}
        </span>
      </div>
    </div>
  );
}

const WATCH_KIND = { breakout_watch: "Breakout watch", pullback_watch: "Pullback watch" };

export function EquitiesDiscover() {
  const { data } = useQuery({ queryKey: ["eq-discover"], queryFn: api.eqDiscover, refetchInterval: eqPoll(60000) });
  const groups = data?.groups || {};
  const labels = data?.labels || {};
  const order = ["plan", "setup", "rel_strength", "uptrend"];
  const keys = order.filter((k) => groups[k]?.length);
  const watch = data?.watch || [];

  return (
    <div className="sn-stack" style={{ gap: "var(--space-5)" }} data-testid="equities-discover">
      <Panel flush title="Tomorrow's triggers" icon="Target" meta={<span className="sn-num">{watch.length}</span>}
             data-testid="discover-watch">
        <div className="sn-stack" style={{ gap: "var(--space-1)", padding: "var(--space-3) var(--space-4)" }}>
          <p className="sn-sans-num" style={{ margin: 0, color: "var(--text-secondary)" }}>
            {watch.length ? `${watch.length} uptrend, top-momentum stocks are one daily close from a setup · as of ${data?.as_of}`
              : (data?.note || "No stocks one close away from a setup in the latest scan.")}
          </p>
          <p className="sn-small sn-faint" style={{ margin: 0 }}>
            Set an alert at the level. A daily close through it (with the volume shown for breakouts) makes the next scan issue a plan.
            Intraday crosses are flagged, but only the close counts.
          </p>
        </div>
        {watch.length > 0 && (
          <div className="sn-table-wrap" style={{ border: 0, borderRadius: 0 }}>
            <table className="sn-table" aria-label="Tomorrow's triggers">
              <thead><tr>
                <th>Symbol</th><th>Watch</th><th className="r">Close</th><th className="r">Level</th>
                <th className="r">Distance</th><th className="r">Momentum</th><th>Confirms if</th><th>Now</th>
              </tr></thead>
              <tbody>
                {watch.map((w) => (
                  <tr key={w.symbol} data-testid={`watch-${w.symbol}`}>
                    <td className="id"><Link to={`/equities/s/${encodeURIComponent(w.symbol)}`} className="sn-link">{w.symbol}</Link></td>
                    <td>{WATCH_KIND[w.kind] || w.kind}</td>
                    <td className="num">{px(w.close)}</td>
                    <td className="num" style={{ color: "var(--text-primary)" }}>{px(w.level)}</td>
                    <td className="num">{isNum(w.distance_pct) ? fmt(w.distance_pct, "pct") : "—"}</td>
                    <td className="num">{w.rs_pct != null ? `P${Number(w.rs_pct).toFixed(0)}` : "—"}</td>
                    <td style={{ whiteSpace: "normal" }}>{w.condition}</td>
                    <td style={{ whiteSpace: "normal" }}>
                      {w.live ? (w.live.crossing ? <Badge icon="ArrowUpRight">Crossing</Badge>
                                                 : <span className="sn-small sn-faint">{w.live.state}</span>) : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>

      <Panel title="Near misses" icon="Filter">
        <div className="sn-stack" style={{ gap: "var(--space-1)" }}>
          <p className="sn-sans-num" style={{ margin: 0, color: "var(--text-secondary)" }}>
            {data?.total ? `${data.total} liquid stocks are one rule away from qualifying · as of ${data.as_of}`
              : "No near-misses in the latest scan."}
          </p>
          <p className="sn-small sn-faint" style={{ margin: 0 }}>
            These are not recommendations. Each passed liquidity, price and history, but missed exactly one swing rule.
          </p>
        </div>
      </Panel>

      {keys.length === 0 ? (
        <EmptyState icon="Filter" title="Nothing one rule away right now" data-testid="discover-empty">
          Run a fresh scan from the Setups tab.
        </EmptyState>
      ) : keys.map((k) => (
        <Panel key={k} flush title={labels[k] || k} meta={<span className="sn-num">{groups[k].length}</span>}
               data-testid={`discover-group-${k}`}>
          <div className="sn-table-wrap" style={{ border: 0, borderRadius: 0 }}>
            <table className="sn-table" aria-label={labels[k] || k}>
              <thead><tr>
                <th>Symbol</th><th className="r">Momentum</th><th>Missed rule</th>
                <th className="r">Close</th><th>Pattern</th>
              </tr></thead>
              <tbody>
                {groups[k].map((x) => (
                  <tr key={x.symbol} data-testid={`discover-card-${x.symbol}`}>
                    <td className="id">{x.symbol}</td>
                    <td className="num">{x.rs_pct != null ? `P${Number(x.rs_pct).toFixed(0)}` : <Delta value={(x.rs || 0) * 100} kind="pct" />}</td>
                    <td style={{ whiteSpace: "normal", color: "var(--text-primary)" }}>{x.first_fail_detail || labels[k]}</td>
                    <td className="num">{px(x.close)}</td>
                    <td>{(x.pullback || x.breakout) ? "Pattern present" : "No pattern"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Panel>
      ))}
    </div>
  );
}

// ── tape page ────────────────────────────────────────────────────────────────

// ── ops card (visible on Tape) ──────────────────────────────────────────────

function fmtDelta(iso) {
  if (!iso) return "—";
  const ms = Date.now() - new Date(iso).getTime();
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.round(s / 60)}m ago`;
  if (s < 86400) return `${Math.round(s / 3600)}h ago`;
  return `${Math.round(s / 86400)}d ago`;
}

function Sparkline({ counts, days, testid, onBarClick }) {
  const W = 60, H = 14, PAD = 1;
  if (!counts || counts.length === 0) {
    return <span className="sn-faint" data-testid={testid}>—</span>;
  }
  const max = Math.max(...counts, 1);
  const n = counts.length;
  const barW = (W - PAD * (n - 1)) / n;
  const title = (days || []).map((d, i) => `${d}: ${counts[i]}`).join("\n");
  return (
    <svg width={W} height={H} data-testid={testid} role="img" aria-label={`Requests per day: ${title.replace(/\n/g, ", ")}`}
         style={{ display: "inline-block", verticalAlign: "middle", cursor: onBarClick ? "pointer" : "default" }}>
      <title>{title}</title>
      {counts.map((c, i) => {
        const h = c > 0 ? Math.max(1, (c / max) * H) : 1;
        return (
          <rect key={i} x={i * (barW + PAD)} y={H - h} width={barW} height={h}
                fill={c > 0 ? "var(--viz-seq-4)" : "var(--border-strong)"}
                data-testid={`${testid}-bar-${i}`}
                onClick={onBarClick && c > 0 ? (e) => { e.stopPropagation(); onBarClick(i); } : undefined}>
            <title>{days?.[i]}: {c} requests</title>
          </rect>
        );
      })}
    </svg>
  );
}

function HttpResult({ code }) {
  if (!code) return <span className="sn-faint">—</span>;
  if (code >= 400) return <RuleResult result="FAIL">{code}</RuleResult>;
  if (code === 200) return <RuleResult result="PASS">{code}</RuleResult>;
  return <RuleResult result="NA">{code}</RuleResult>;
}

function OpsDrilldownModal({ product, date, onClose }) {
  const { data, isLoading } = useQuery({
    queryKey: ["ops-detail", product, date],
    queryFn: () => api.eqOpsDetail(product, date),
    enabled: !!(product && date),
  });
  const copyCsv = () => {
    if (!data?.rows?.length) return;
    const header = "endpoint,symbol,http,requests,cost_usd\n";
    const body = data.rows.map((r) => `"${r.endpoint}","${r.symbol}",${r.http},${r.requests},${r.cost_usd}`).join("\n");
    navigator.clipboard.writeText(header + body).then(
      () => toast.success("CSV copied to clipboard"),
      () => toast.error("Copy failed"));
  };
  return (
    <Modal
      width={720}
      onClose={onClose}
      icon="DollarSign"
      data-testid="ops-drilldown-modal"
      title={<>Cost detail · {String(product).toUpperCase()} · <span className="sn-num">{date}</span></>}
      footer={
        <>
          <Button icon="Copy" onClick={copyCsv} data-testid="drilldown-copy-csv">Copy as CSV</Button>
          <Button variant="primary" onClick={onClose} data-testid="drilldown-close">Close</Button>
        </>
      }
    >
      <div className="sn-stack" style={{ maxHeight: "60vh", overflowY: "auto" }}>
        {data && (
          <p className="sn-small sn-muted" style={{ margin: 0 }}>
            <span className="sn-num">{data.total_requests.toLocaleString("en-US")}</span> requests ·{" "}
            <span className="sn-num">${Number(data.total_cost_usd).toFixed(4)}</span> amortized
          </p>
        )}
        {isLoading ? (
          <Skeleton lines={4} />
        ) : !data?.rows?.length ? (
          <p className="sn-muted" style={{ margin: 0 }}>No cost-ledger rows for this day.</p>
        ) : (
          <div className="sn-table-wrap" data-density="compact">
            <table className="sn-table" aria-label="Cost ledger rows">
              <thead>
                <tr>
                  <th>Endpoint</th>
                  <th>Symbol</th>
                  <th className="r">HTTP</th>
                  <th className="r">Requests</th>
                  <th className="r">Cost (USD)</th>
                </tr>
              </thead>
              <tbody>
                {data.rows.map((r, i) => (
                  <tr key={i} data-testid={`drilldown-row-${i}`}>
                    <td className="id">{r.endpoint}</td>
                    <td className="id">{r.symbol}</td>
                    <td className="r"><HttpResult code={r.http} /></td>
                    <td className="num">{r.requests.toLocaleString("en-US")}</td>
                    <td className="num">${Number(r.cost_usd).toFixed(4)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Modal>
  );
}

/** Feed health and API cost: developer detail. Open by default for the owner, folded for everyone else. */
export function EquitiesOpsCard() {
  const { can } = useAuth();
  const [drilldown, setDrilldown] = useState(null);
  const [open, setOpen] = useState(() => can("owner"));
  const { data } = useQuery({
    queryKey: ["equities-ops"],
    queryFn: () => api.eqOps(),
    refetchInterval: eqPoll(30000),
  });
  const products = useMemo(() => (data?.cost_by_product || []).filter((r) => r.product !== "unknown"), [data]);
  if (!data) return null;
  return (
    <Panel
      title="Ops · Massive feed"
      icon="Server"
      data-testid="equities-ops-card"
      actions={
        <Button size="sm" variant="ghost" icon={open ? "ChevronDown" : "ChevronRight"} aria-expanded={open}
                onClick={() => setOpen((o) => !o)} data-testid="ops-toggle">
          {open ? "Hide technical details" : "Technical details"}
        </Button>
      }
      meta={
        <>
          <Badge data-testid="ops-plan-chip">STOCKS PLAN · {String(data.stocks_plan).toUpperCase()}</Badge>
          {data.luld_supported ? (
            <Badge icon="Radio" data-testid="ops-luld-chip" title="Real-time snapshot entitled — halt/LULD signal is live.">LULD · LIVE</Badge>
          ) : (
            <Badge icon="CircleSlash" data-testid="ops-luld-chip" style={{ borderStyle: "dashed" }}
                   title="Snapshot endpoint not entitled on basic plan; halt/LULD chip stays dormant until plan upgrade.">
              LULD · N/A · basic tier
            </Badge>
          )}
        </>
      }
    >
      {!open ? (
        <p className="sn-small sn-muted" style={{ margin: 0 }} data-testid="ops-collapsed-note">
          Feed health, rate limits and the API cost ledger. Nothing here changes a setup.
        </p>
      ) : (
      <div className="sn-stack">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <div className="sn-stat sn-stat-sm">
            <span className="sn-stat-k">Last poll</span>
            <span className="sn-stat-v" data-testid="ops-last-poll">{fmtDelta(data.last_poll_at)}</span>
            {data.last_poll_result && (
              <span className="sn-stat-sub sn-num">
                {data.last_poll_result.live}L · {data.last_poll_result.degraded}D · {data.last_poll_result.unavailable}U
              </span>
            )}
          </div>
          <div className="sn-stat sn-stat-sm">
            <span className="sn-stat-k">Last error</span>
            <span data-testid="ops-last-error" style={{ overflowWrap: "anywhere" }}>
              {data.last_poll_error ? (
                <span className="sn-small" style={{ color: "var(--text-primary)", display: "inline-flex", gap: 4, alignItems: "flex-start" }}>
                  <Icon name="TriangleAlert" size={14} style={{ color: "var(--text-tertiary)", flex: "none", marginTop: 1 }} />
                  {data.last_poll_error}
                </span>
              ) : <RuleResult result="PASS">clean</RuleResult>}
            </span>
          </div>
          <div className="sn-stat sn-stat-sm">
            <span className="sn-stat-k">Rate limit</span>
            <span data-testid="ops-rate-limit">
              {data.rate_limit_cooldown_s > 0 ? (
                <span className="sn-num sn-small" style={{ color: "var(--text-primary)", display: "inline-flex", gap: 4, alignItems: "center" }}>
                  <Icon name="Hourglass" size={14} style={{ color: "var(--text-tertiary)" }} />cooling {data.rate_limit_cooldown_s}s
                </span>
              ) : <RuleResult result="PASS">clear</RuleResult>}
            </span>
            <span className="sn-stat-sub">{(data.blocked_endpoints || []).length} endpoint(s) gated</span>
          </div>
        </div>

        <span className="sn-micro">Cost ledger · trailing {data.cost_window_days}d</span>
        <div className="sn-table-wrap">
          <table className="sn-table" data-testid="ops-cost-table" aria-label="Cost ledger by product">
            <thead>
              <tr>
                <th>Product</th>
                <th className="r">Requests</th>
                <th className="r">Est. USD</th>
                <th className="r">5-day trend</th>
              </tr>
            </thead>
            <tbody>
              {products.length === 0 && (
                <tr><td colSpan="4" className="sn-muted">No API calls yet.</td></tr>
              )}
              {products.map((row) => (
                <tr key={row.product} data-testid={`ops-cost-${row.product}`}>
                  <td className="id">{String(row.product).toUpperCase()}</td>
                  <td className="num">{Number(row.requests).toLocaleString("en-US")}</td>
                  <td className="num" style={row.cost_usd > 0 ? null : { color: "var(--text-tertiary)" }}>
                    ${Number(row.cost_usd).toFixed(4)}
                  </td>
                  <td className="r">
                    <Sparkline counts={(data.daily_counts_by_product || {})[row.product] || []}
                               days={data.spark_day_axis || []}
                               testid={`ops-sparkline-${row.product}`}
                               onBarClick={(i) => {
                                 const d = (data.spark_day_axis || [])[i];
                                 if (d) setDrilldown({ product: row.product, date: d });
                               }} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {(data.blocked_endpoints || []).length > 0 && (
          <p className="sn-small sn-muted" style={{ margin: 0 }}>
            <span style={{ color: "var(--text-primary)", fontWeight: 500 }}>Gated endpoints:</span>{" "}
            <span className="sn-num">{(data.blocked_endpoints || []).join(" · ")}</span>
          </p>
        )}
        <p className="sn-small sn-faint" style={{ margin: 0 }}>
          Cost is amortized from plan subscriptions — Basic Stocks ≈ <span className="sn-num">${data.polygon_rates?.stocks_basic}</span>/req,
          Advanced Stocks ≈ <span className="sn-num">${data.polygon_rates?.stocks_advanced}</span>/req. Nominal, for capacity planning only.
          Select any sparkline bar to open that day&apos;s ledger.
        </p>
      </div>
      )}
      {drilldown && (
        <OpsDrilldownModal product={drilldown.product} date={drilldown.date}
                           onClose={() => setDrilldown(null)} />
      )}
    </Panel>
  );
}

/** Halt / LULD indicator. Halts are market notices, not system alarms. */
function HaltBadge({ halt, ...rest }) {
  if (halt === "HALT") return <Badge className="sn-sys-notice" icon="Ban" {...rest}>HALT</Badge>;
  if (halt === "LULD") return <Badge className="sn-sys-notice" icon="TriangleAlert" {...rest}>LULD</Badge>;
  if (halt && halt !== "OK") return <Badge className="sn-sys-notice" icon="Info" {...rest}>{halt}</Badge>;
  return <Badge icon="CircleCheck" {...rest}>NOT HALTED</Badge>;
}

export function EquitiesTape() {
  const qc = useQueryClient();
  const nav = useNavigate();
  const { data, isLoading, error } = useQuery({
    queryKey: ["equities-tape"],
    queryFn: () => api.eqTape(),
    refetchInterval: eqPoll(30000),
  });
  const refresh = useMutation({
    mutationFn: () => api.eqRefresh(),
    onSuccess: (r) => {
      toast.success(`Refreshed: ${r.live} live · ${r.degraded} degraded · ${r.unavailable} unavailable`);
      qc.invalidateQueries({ queryKey: ["equities-tape"] });
      qc.invalidateQueries({ queryKey: ["equities-ops"] });
    },
    onError: (e) => toast.error(fmtErr(e.response?.data?.detail) || e.message),
  });
  if (isLoading) return <PageLoading label="Loading tape" />;
  if (error) return <ErrorState title="The tape failed to load">{String(error.message)}</ErrorState>;

  const exportXlsx = async () => {
    try {
      const blob = await api.eqExport();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Equities_Watchlist_Sentinel_${new Date().toISOString().slice(0,10)}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Watchlist exported");
    } catch (e) { toast.error(fmtErr(e.response?.data?.detail) || e.message); }
  };

  return (
    <div className="sn-stack" style={{ gap: "var(--space-5)" }}>
      <Panel
        flush
        title="Watchlist tape"
        icon="Activity"
        data-testid="equities-tape-panel"
        meta={
          <>
            <RecencyBadge recency={data?.recency} data-testid="equities-recency" />
            <span className="sn-row" style={{ gap: 4 }}>
              <Icon name="Clock" size={12} />
              <span className="sn-num">{data?.last_refresh ? etSyd(data.last_refresh) : "no refresh yet"}</span>
            </span>
          </>
        }
        actions={
          <>
            <Button size="sm" icon="Download" onClick={exportXlsx} data-testid="equities-export">Export</Button>
            <Button size="sm" icon="RefreshCw" onClick={() => refresh.mutate()} loading={refresh.isPending}
                    data-testid="equities-refresh">
              {refresh.isPending ? "Refreshing…" : "Refresh"}
            </Button>
          </>
        }
      >
        {data?.recency === "STUB" && (
          <div style={{ padding: "var(--space-3) var(--space-4) 0" }}>
            <DataBanner kind="stub" inline>
              Real-time NBBO is not entitled on the current Massive plan. Prior close and the latest daily bar populate; bid and ask stay UNAVAILABLE until the plan is upgraded.
            </DataBanner>
          </div>
        )}
        <div style={{ overflowX: "auto" }}>
          <table className="sn-table" aria-label="Watchlist tape">
            <thead>
              <tr>
                <th className="sticky-col">Symbol</th>
                <th className="r">Last</th>
                <th className="r">Net</th>
                <th className="r">%</th>
                <th className="r hidden md:table-cell">Bid</th>
                <th className="r hidden md:table-cell">Ask</th>
                <th className="r hidden md:table-cell">Day high</th>
                <th className="r hidden md:table-cell">Day low</th>
                <th className="r hidden lg:table-cell">VWAP</th>
                <th className="r hidden lg:table-cell">Age</th>
                <th className="hidden lg:table-cell">Recency</th>
              </tr>
            </thead>
            <tbody>
              {(data?.rows || []).map((row) => {
                const ch = priceChange(row?.last, row?.prior_close);
                const rec = String(row?.recency || "UNAVAILABLE").toUpperCase();
                const numState = REC_NUM_STATE[rec];
                return (
                  <tr key={row.symbol} onClick={() => nav(symbolPath(row.symbol))}
                      data-testid={`equities-row-${row.symbol}`}
                      style={{ cursor: "pointer" }}>
                    <td className="id sticky-col">
                      <Link to={symbolPath(row.symbol)} className="sn-link" style={{ fontSize: 12, fontWeight: 500 }}
                            onClick={(e) => e.stopPropagation()}>
                        {row.symbol}
                      </Link>
                    </td>
                    <td className="num">
                      {numState ? <Num value={isNum(row?.last) ? Number(row.last) : null} kind="price" state={numState} />
                        : <span className="sn-faint">{px(row?.last)}</span>}
                    </td>
                    <td className="num">{ch ? <Delta value={ch.net} kind="price" /> : <span className="sn-faint">—</span>}</td>
                    <td className="num">{ch ? <span className={dirClass(ch.pct)}>{fmt(ch.pct, "pct", { signed: true })}</span> : <span className="sn-faint">—</span>}</td>
                    <td className="num hidden md:table-cell"
                        title={!row?.bid ? "NBBO not entitled on current Massive plan" : ""}>
                      {row?.bid ? px(row.bid) : <Num value={null} state="unavailable" />}
                    </td>
                    <td className="num hidden md:table-cell">
                      {row?.ask ? px(row.ask) : <Num value={null} state="unavailable" />}
                    </td>
                    <td className="num hidden md:table-cell">{px(row?.day_high)}</td>
                    <td className="num hidden md:table-cell">{px(row?.day_low)}</td>
                    <td className="num hidden lg:table-cell">{px(row?.vwap)}</td>
                    <td className="num hidden lg:table-cell" style={{ color: "var(--text-secondary)" }}>{fmtAge(row?.quote_ts)}</td>
                    <td className="hidden lg:table-cell">
                      <span className="sn-row" style={{ gap: 4, flexWrap: "nowrap" }}>
                        <RecencyBadge recency={rec} detail={null} />
                        {row?.halt && row.halt !== "OK" && (
                          <HaltBadge halt={row.halt} data-testid={`row-halt-${row.symbol}`} />
                        )}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <p className="sn-small sn-faint" style={{ margin: 0, padding: "var(--space-3) var(--space-4)", borderTop: "1px solid var(--border-hairline)" }}>
          Select a row to open the symbol detail. The server re-polls every 60s; Refresh forces an immediate re-poll.
          Sequential polls stay under the basic-tier rate limit — the full watchlist takes about 5–10s to refresh.
        </p>
      </Panel>
      <EquitiesOpsCard />
    </div>
  );
}

// ── admin (watchlist editor) ────────────────────────────────────────────────

export function EquitiesAdmin() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const [draft, setDraft] = useState(null);
  const { data: wl } = useQuery({
    queryKey: ["equities-watchlist"],
    queryFn: () => api.eqGetWatchlist(),
  });
  const save = useMutation({
    mutationFn: (symbols) => api.eqPutWatchlist({ symbols }),
    onSuccess: () => {
      toast.success("Watchlist saved");
      qc.invalidateQueries({ queryKey: ["equities-watchlist"] });
      qc.invalidateQueries({ queryKey: ["equities-tape"] });
      setDraft(null);
    },
    onError: (e) => toast.error(fmtErr(e.response?.data?.detail) || e.message),
  });
  const symbols = draft ?? wl?.symbols ?? [];
  const isOwner = user?.role === "owner";

  return (
    <Panel
      title="Watchlist"
      icon="ListChecks"
      data-testid="equities-admin-panel"
      meta={<span><span className="sn-num">{symbols.length} / 15</span> symbols</span>}
    >
      <div className="sn-stack">
        {!isOwner && (
          <p className="sn-row sn-small sn-muted" style={{ margin: 0, gap: "var(--space-1)" }}>
            <Icon name="Lock" size={14} />Read-only. Only an owner can edit the watchlist.
          </p>
        )}
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
          {symbols.map((s, i) => (
            <div key={i} className="sn-row" style={{ gap: "var(--space-1)", flexWrap: "nowrap", alignItems: "flex-end" }}>
              <TextField value={s} disabled={!isOwner} aria-label={`Symbol ${i + 1}`} className="flex-1"
                         style={{ fontFamily: "var(--font-mono)" }}
                         onChange={(e) => {
                           const next = [...symbols]; next[i] = e.target.value.toUpperCase();
                           setDraft(next);
                         }}
                         data-testid={`equities-sym-${i}`} />
              {isOwner && (
                <IconButton icon="X" size="sm" label={`Remove ${s || `symbol ${i + 1}`}`}
                            onClick={() => setDraft(symbols.filter((_, j) => j !== i))}
                            data-testid={`equities-remove-${i}`} />
              )}
            </div>
          ))}
        </div>
        {isOwner && (
          <div className="sn-row">
            <Button icon="Plus"
                    disabled={symbols.length >= 15}
                    onClick={() => setDraft([...symbols, ""])}
                    data-testid="equities-add">Add symbol</Button>
            <Button variant="primary"
                    disabled={!draft || save.isPending}
                    loading={save.isPending}
                    onClick={() => save.mutate(symbols.filter((s) => s && s.trim()))}
                    data-testid="equities-save">
              {save.isPending ? "Saving…" : "Save"}
            </Button>
            {draft && (
              <Button variant="ghost" onClick={() => setDraft(null)}
                      data-testid="equities-cancel">Cancel</Button>
            )}
          </div>
        )}
      </div>
    </Panel>
  );
}

// ── symbol detail ────────────────────────────────────────────────────────────

/** Chart colours come from the live tokens (lwcOptions); re-applied whenever the theme changes. */
function SymbolChart({ symbol, chartInstanceRef }) {
  const ref = useRef(null);
  const paintRef = useRef(null);
  const { theme } = usePrefs() || {};
  const { data } = useQuery({
    queryKey: ["equities-bars", symbol],
    queryFn: () => api.eqBars(symbol),
    refetchInterval: eqPoll(60000),
  });
  useEffect(() => {
    if (!data || !ref.current) return;
    const el = ref.current;
    el.innerHTML = "";
    const o = lwcOptions();
    const chart = createChart(el, {
      width: el.clientWidth,
      height: 340,
      layout: o.layout,
      grid: o.grid,
      rightPriceScale: o.rightPriceScale,
      timeScale: { ...o.timeScale, timeVisible: !data.fallback },
      crosshair: o.crosshair,
    });
    if (chartInstanceRef) chartInstanceRef.current = chart;
    const candle = (opts) => ({
      upColor: opts.series.up, downColor: opts.series.down, borderUpColor: opts.series.up,
      borderDownColor: opts.series.down, wickUpColor: opts.series.up, wickDownColor: opts.series.down,
    });
    const series = chart.addCandlestickSeries(candle(o));
    // Massive returns `t` in ms — lightweight-charts wants seconds for time.
    series.setData((data.bars || [])
      .filter((b) => b.t && b.o && b.c)
      .map((b) => ({ time: Math.floor(b.t / 1000), open: b.o, high: b.h, low: b.l, close: b.c })));
    const ann = data.annotations || {};
    // VWAP: viz-2 dashed. OR15: band colour, dotted. OR30: band colour, large dash.
    const lines = [];
    const addLine = (price, kind, lineStyle, title) => {
      const color = kind === "vwap" ? o.series.vwap.color : o.series.orBand.color;
      lines.push({ kind, line: series.createPriceLine({ price, color, lineWidth: 1, lineStyle, title }) });
    };
    if (ann.vwap) addLine(ann.vwap, "vwap", 2, "VWAP");
    if (ann.or15_high) addLine(ann.or15_high, "or", 1, "OR15 H");
    if (ann.or15_low) addLine(ann.or15_low, "or", 1, "OR15 L");
    if (ann.or30_high) addLine(ann.or30_high, "or", 3, "OR30 H");
    if (ann.or30_low) addLine(ann.or30_low, "or", 3, "OR30 L");
    chart.timeScale().fitContent();
    paintRef.current = () => {
      const t = lwcOptions();
      chart.applyOptions({ layout: t.layout, grid: t.grid, crosshair: t.crosshair, rightPriceScale: t.rightPriceScale,
                           timeScale: { borderColor: t.timeScale.borderColor } });
      series.applyOptions(candle(t));
      lines.forEach(({ kind, line }) => line.applyOptions({ color: kind === "vwap" ? t.series.vwap.color : t.series.orBand.color }));
    };
    const onResize = () => chart.applyOptions({ width: el.clientWidth });
    window.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("resize", onResize);
      paintRef.current = null;
      if (chartInstanceRef) chartInstanceRef.current = null;
      chart.remove();
    };
  }, [data, chartInstanceRef]);
  // The theme attribute lands on <html> after this render commits; repaint on the next frame.
  useEffect(() => {
    const id = requestAnimationFrame(() => paintRef.current && paintRef.current());
    return () => cancelAnimationFrame(id);
  }, [theme]);

  if (!data) {
    return <Panel title="Chart" icon="ChartCandlestick"><Skeleton height={340} /></Panel>;
  }
  const barCount = (data.bars || []).length;
  const ann = data.annotations || {};
  const sample = (style) => <span aria-hidden="true" style={{ display: "inline-block", width: 16, height: 0, borderTop: style }} />;
  return (
    <Panel
      title={data.fallback ? "Chart · daily (intraday not entitled on this plan)" : "Chart · 1-minute"}
      icon="ChartCandlestick"
      meta={<><span className="sn-num">{barCount} bars</span><RecencyBadge recency={data.recency} detail={null} /></>}
    >
      <div className="sn-stack">
        {barCount === 0 ? (
          <EmptyState icon="CalendarOff" title="No bars for this session">
            Weekend, market holiday, or the bars endpoint is gated on this plan.
          </EmptyState>
        ) : (
          <div ref={ref} data-testid="equity-chart" style={{ width: "100%" }} role="img"
               aria-label={`${symbol} ${data.fallback ? "daily" : "1-minute"} candles, ${barCount} bars${ann.vwap ? `, VWAP ${px(ann.vwap)}` : ""}. Data ${data.recency || "UNAVAILABLE"}.`} />
        )}
        <div className="sn-row sn-small sn-muted" style={{ gap: "var(--space-4)" }}>
          <span className="sn-row" style={{ gap: 6 }}>{sample("1.5px dashed var(--viz-2)")}VWAP</span>
          <span className="sn-row" style={{ gap: 6 }}>{sample("1.5px dotted var(--viz-band)")}OR15 H/L</span>
          <span className="sn-row" style={{ gap: 6 }}>{sample("1.5px dashed var(--viz-band)")}OR30 H/L</span>
          <span className="sn-faint">Markers are annotations, not forecasts.</span>
        </div>
      </div>
    </Panel>
  );
}

export function EquitiesSymbol() {
  const { symbol: rawSymbol } = useParams();
  const symbol = String(rawSymbol || "").toUpperCase();
  usePageTitle(symbol ? `${symbol} · Equities` : "Equities");
  const qc = useQueryClient();
  const chartRef = useRef(null);
  const [attachChart, setAttachChart] = useState(true);
  const { data } = useQuery({
    queryKey: ["equities-symbol", symbol],
    queryFn: () => api.eqSymbol(symbol),
    enabled: !!symbol,
    refetchInterval: eqPoll(30000),
  });
  const { data: pins } = useQuery({
    queryKey: ["equities-pins", symbol],
    queryFn: () => api.eqPins(symbol),
  });
  const pin = useMutation({
    mutationFn: async () => {
      let chart_png_base64 = null;
      if (attachChart && chartRef.current) {
        try {
          const canvas = chartRef.current.takeScreenshot();
          chart_png_base64 = canvas.toDataURL("image/png");
        } catch (e) {
          console.warn("chart screenshot failed", e);
        }
      }
      return api.eqPin(symbol, { note: null, chart_png_base64 });
    },
    onSuccess: (r) => {
      toast.success(`${symbol} snapshot pinned${r.chart_path ? " with chart" : ""}`, { duration: 4000 });
      qc.invalidateQueries({ queryKey: ["equities-pins", symbol] });
    },
    onError: (e) => toast.error(fmtErr(e.response?.data?.detail) || e.message),
  });
  if (!data) return <PageLoading label={`Loading ${symbol}`} />;
  const ch = priceChange(data.last, data.prior_close);
  const lastState = REC_NUM_STATE[String(data.recency || "").toUpperCase()] || "live";

  return (
    <div className="sn-stack" style={{ gap: "var(--space-5)" }} data-testid="equities-detail">
      <Panel>
        <div className="sn-stack" style={{ gap: "var(--space-4)" }}>
          <div className="sn-row" style={{ justifyContent: "space-between", alignItems: "flex-start", gap: "var(--space-4)" }}>
            <div className="sn-stack" style={{ gap: "var(--space-2)" }}>
              <h2 className="sn-num text-title" style={{ margin: 0, fontWeight: 600, color: "var(--text-primary)" }}>{symbol}</h2>
              <div className="sn-row">
                <RecencyBadge recency={data.recency} data-testid="equities-recency" />
                <HaltChip halt={data.halt} recency={data.recency} />
              </div>
            </div>
            <div className="sn-row" style={{ gap: "var(--space-5)", alignItems: "flex-start" }}>
              <div className="sn-stat" style={{ alignItems: "flex-end" }}>
                <span className="sn-stat-k">Last</span>
                <span className="sn-stat-v">{isNum(data.last) ? <Num value={Number(data.last)} kind="price" state={lastState} /> : "—"}</span>
                <span className="sn-stat-sub">
                  {ch ? <><Delta value={ch.net} kind="price" /><span className={dirClass(ch.pct)}>{fmt(ch.pct, "pct", { signed: true })}</span></> : "—"}
                </span>
              </div>
              <div className="sn-stack" style={{ gap: "var(--space-2)", alignItems: "flex-end" }}>
                <Button variant="primary" icon="Pin" onClick={() => pin.mutate()} loading={pin.isPending}
                        data-testid="equities-pin">
                  {pin.isPending ? "Pinning…" : "Pin snapshot"}
                </Button>
                <span data-testid="pin-attach-chart-label">
                  <Checkbox label="Attach chart PNG" checked={attachChart} onChange={setAttachChart}
                            data-testid="pin-attach-chart" />
                </span>
              </div>
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <StatCell size="sm" label="Prior close" value={px(data.prior_close)} />
            <StatCell size="sm" label="Day open" value={px(data.day_open)} />
            <StatCell size="sm" label="Day high" value={px(data.day_high)} />
            <StatCell size="sm" label="Day low" value={px(data.day_low)} />
            <StatCell size="sm" label="VWAP" value={px(data.vwap)} />
            <StatCell size="sm" label="Volume" value={data.volume ? Number(data.volume).toLocaleString("en-US") : "—"} />
            <StatCell size="sm" label="Bid" value={data.bid ? px(data.bid) : null} state={data.bid ? "live" : "unavailable"} />
            <StatCell size="sm" label="Ask" value={data.ask ? px(data.ask) : null} state={data.ask ? "live" : "unavailable"} />
          </div>
        </div>
      </Panel>

      <SymbolChart symbol={symbol} chartInstanceRef={chartRef} />

      {pins?.pins?.length > 0 && (
        <Panel flush title={<>Pinned snapshots · <span className="sn-num">{symbol}</span></>} icon="Pin"
               meta={<span>append-only</span>}>
          <div style={{ overflowX: "auto" }}>
            <table className="sn-table" aria-label={`Pinned snapshots for ${symbol}`}>
              <thead><tr>
                <th>Pinned at</th><th>By</th>
                <th>Chart</th>
                <th className="r">Last</th>
                <th className="r">Prior</th>
                <th className="r hidden sm:table-cell">Day high</th>
                <th className="r hidden sm:table-cell">Day low</th>
                <th className="hidden md:table-cell">Note</th>
              </tr></thead>
              <tbody>
                {pins.pins.map((p) => (
                  <tr key={p.id} data-testid={`pin-row-${p.id.slice(0, 8)}`}>
                    <td className="sn-num" style={{ fontSize: 12, color: "var(--text-primary)" }}>{dateTimeET(p.pinned_at).slice(0, 10)} {etSyd(p.pinned_at)}</td>
                    <td>{p.pinned_by}</td>
                    <td>
                      {p.chart_path ? (
                        <PinChartThumb pinId={p.id} />
                      ) : (
                        <span className="sn-faint">—</span>
                      )}
                    </td>
                    <td className="num">{px(p.payload?.last)}</td>
                    <td className="num">{px(p.payload?.prior_close)}</td>
                    <td className="num hidden sm:table-cell">{px(p.payload?.day_high)}</td>
                    <td className="num hidden sm:table-cell">{px(p.payload?.day_low)}</td>
                    <td className="hidden md:table-cell">{p.note || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Panel>
      )}
    </div>
  );
}

function HaltChip({ halt, recency }) {
  // Dormant on basic tier — snapshot endpoint isn't entitled so halt is always
  // null. Once the plan upgrades this will auto-light HALT / LULD / OK.
  if (!halt) {
    return (
      <Badge icon="CircleSlash" data-testid="halt-chip" style={{ borderStyle: "dashed" }}
             title="LULD / halt indicator requires Advanced Stocks entitlement. Dormant on the basic tier.">
        HALT/LULD · N/A
      </Badge>
    );
  }
  return <HaltBadge halt={halt === "HALT" || halt === "LULD" ? halt : "OK"} data-testid="halt-chip" />;
}

function PinChartThumb({ pinId }) {
  const [url, setUrl] = useState(null);
  const [gone, setGone] = useState(false);
  useEffect(() => {
    let dead = false;
    let objUrl = null;
    client.get(`/v1/equities/pins/${pinId}/chart.png`, { responseType: "blob" })
      .then((r) => { if (!dead) { objUrl = URL.createObjectURL(r.data); setUrl(objUrl); } })
      .catch((e) => { if (!dead && e.response?.status === 410) setGone(true); });
    return () => { dead = true; if (objUrl) URL.revokeObjectURL(objUrl); };
  }, [pinId]);
  if (gone) return <span className="sn-faint" title="Saved before the move to Railway; no longer available">n/a</span>;
  if (!url) return <span className="sn-faint">…</span>;
  return (
    <a href={url} target="_blank" rel="noreferrer" data-testid={`pin-chart-${pinId.slice(0, 8)}`} className="sn-row">
      <img src={url} alt="Pinned chart" style={{ height: 34, width: 56, objectFit: "cover", borderRadius: "var(--radius-xs)", border: "1px solid var(--border-hairline)" }} />
    </a>
  );
}
